import type { Lead } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@up2stay/up2promo-ui';
import { Loader2, Plus, Tag, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { LEAD_DETAIL_SECTION_TITLE_CLASS } from '@/core/components/leads/lead-detail/lead-detail-section-card';
import {
  keyboardAwarePopoverCollision,
  useCssKeyboardInset,
} from '@/core/hooks/use-css-keyboard-inset';

export type LeadDetailTagsLabels = {
  title: string;
  empty: string;
  add: string;
  remove: string;
  placeholder: string;
  apply: string;
  existingTags: string;
  noExistingTags: string;
  readOnlyHint?: string;
};

type LeadDetailTagsProps = {
  lead: Lead;
  /** Workspace tags to pick from (already excluding Event legacy strings). */
  existingTags: string[];
  editable: boolean;
  isUpdating: boolean;
  labels: LeadDetailTagsLabels;
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
  /** Compact label + chips for Zuordnung sidebar (no section card chrome). */
  embedded?: boolean;
};

export function LeadDetailTags({
  lead,
  existingTags,
  editable,
  isUpdating,
  labels,
  onAdd,
  onRemove,
  embedded = false,
}: LeadDetailTagsProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const keyboardInset = useCssKeyboardInset(open);

  const currentTags = useMemo(() => {
    const seen = new Set<string>();
    const tags: string[] = [];
    for (const raw of lead.tags ?? []) {
      const tag = String(raw ?? '').trim();
      if (!tag) continue;
      const key = tag.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      tags.push(tag);
    }
    return tags;
  }, [lead.tags]);

  const currentKeys = useMemo(
    () => new Set(currentTags.map((t) => t.toLowerCase())),
    [currentTags],
  );

  const pickableTags = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return existingTags.filter((tag) => {
      const key = tag.toLowerCase();
      if (currentKeys.has(key)) return false;
      if (!q) return true;
      return key.includes(q);
    });
  }, [existingTags, currentKeys, draft]);

  const apply = (tag: string) => {
    const next = tag.trim();
    if (!next) return;
    if (currentKeys.has(next.toLowerCase())) return;
    onAdd(next);
    setDraft('');
    setOpen(false);
  };

  return (
    <section className={cn('flex flex-col', embedded ? 'gap-1.5' : 'gap-3')}>
      <div className="flex items-center justify-between gap-2">
        <h3
          className={
            embedded
              ? 'text-[12px] font-normal text-muted-foreground'
              : LEAD_DETAIL_SECTION_TITLE_CLASS
          }
        >
          {labels.title}
        </h3>
        {editable ? (
          <Popover
            modal={false}
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (!next) setDraft('');
            }}
          >
            <PopoverTrigger asChild>
              {embedded ? (
                <button
                  type="button"
                  disabled={isUpdating}
                  className="inline-flex h-6 items-center gap-1 rounded-[7px] border border-dashed border-[color:var(--surface-border)] px-2 text-[12px] text-muted-foreground"
                >
                  {isUpdating ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <Plus className="size-3" />
                  )}
                  Tag
                </button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-2 h-4 w-4" />
                  )}
                  {labels.add}
                </Button>
              )}
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-72 space-y-2 p-3"
              collisionPadding={keyboardAwarePopoverCollision(keyboardInset)}
            >
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={labels.placeholder}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    apply(draft);
                  }
                }}
              />
              <div className="space-y-1">
                <p className="px-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {labels.existingTags}
                </p>
                <div className="max-h-40 space-y-0.5 overflow-y-auto">
                  {pickableTags.length === 0 ? (
                    <p className="px-1 py-1.5 text-sm text-muted-foreground">
                      {labels.noExistingTags}
                    </p>
                  ) : (
                    pickableTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className="flex w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                        onClick={() => apply(tag)}
                      >
                        {tag}
                      </button>
                    ))
                  )}
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                className="w-full"
                disabled={!draft.trim()}
                onClick={() => apply(draft)}
              >
                {labels.apply}
              </Button>
            </PopoverContent>
          </Popover>
        ) : labels.readOnlyHint ? (
          <p className="max-w-[16rem] text-right text-xs text-muted-foreground">
            {labels.readOnlyHint}
          </p>
        ) : null}
      </div>

      {currentTags.length === 0 ? (
        embedded ? (
          <div className="flex flex-wrap gap-1.5">
            {editable ? null : (
              <p className="text-[12.5px] text-muted-foreground">
                {labels.empty}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{labels.empty}</p>
        )
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {currentTags.map((tag) => (
            <Badge
              key={tag}
              variant="outline"
              className={cn(
                'max-w-full gap-1 truncate font-normal',
                embedded &&
                  'h-6 border-transparent bg-muted px-2.5 text-[12px]',
              )}
              title={tag}
            >
              {!embedded ? (
                <Tag className="h-3 w-3 shrink-0 opacity-70" />
              ) : null}
              <span className="truncate">{tag}</span>
              {editable ? (
                <button
                  type="button"
                  className="ml-0.5 rounded-sm p-0.5 hover:bg-muted"
                  aria-label={labels.remove}
                  disabled={isUpdating}
                  onClick={() => onRemove(tag)}
                >
                  <X className="h-3 w-3 opacity-70" />
                </button>
              ) : null}
            </Badge>
          ))}
        </div>
      )}
    </section>
  );
}
